<template>
    <div
        class="menu-item-container"
        data-cy="menuItem"
    >
        <router-link
            v-if="to"
            v-slot="{href: linkHref, navigate}"
            :to="to"
        >
            <div
                :id="id"
                :class="['menu-item', {active, disabled, internal, open: hasDropdown() && localOpen}]"
            >
                <button
                    v-if="hasDropdown()"
                    :aria-expanded="hasDropdown() ? open ? 'true' : 'false' : undefined"
                    :aria-controls="`${id}Dropdown`"
                    class="menu-link"
                    :disabled="disabled"
                    @click.prevent="handleClick($event, id, hasDropdown())"
                >
                    <slot />

                    <FontAwesomeIcon
                        v-if="hasDropdown()"
                        class="icon"
                        icon="chevron-down"
                        size="sm"
                    />
                </button>
                <a
                    v-else
                    class="menu-link"
                    :disabled="disabled"
                    :href="linkHref"
                    @click="navigate"
                >
                    <!-- Content to be displayed if the item is an internal link -->
                    <slot />
                </a>

                <b-collapse
                    v-if="hasDropdown()"
                    :id="`${id}Dropdown`"
                    class="menu-dropdown"
                    :visible="active"
                >
                    <!-- Dropdown items to be displayed if the item is an internal link -->
                    <slot name="dropdown-items" />
                </b-collapse>
            </div>
        </router-link>

        <div
            v-else
            :id="id"
            :class="['menu-item', {active, disabled, internal, open: hasDropdown() && localOpen}]"
        >
            <a
                v-if="href"
                class="menu-link"
                :disabled="disabled"
                :href="href"
                :rel="rel"
                :target="target"
            >
                <!-- Content to be displayed if the item is a custom/external link -->
                <slot />

                <FontAwesomeIcon
                    v-if="hasDropdown()"
                    class="icon"
                    icon="chevron-down"
                    size="sm"
                />
            </a>

            <button
                v-else
                :aria-expanded="hasDropdown() && localOpen ? 'true' : 'false'"
                :aria-controls="hasDropdown() ? `${id}Dropdown` : undefined"
                class="menu-link"
                :disabled="disabled"
                @click="handleClick($event, id, hasDropdown())"
            >
                <!-- Content to be displayed if the item is a button -->
                <slot />

                <FontAwesomeIcon
                    v-if="hasDropdown()"
                    class="icon"
                    icon="chevron-down"
                    size="sm"
                />
            </button>

            <b-collapse
                v-if="hasDropdown()"
                :id="`${id}Dropdown`"
                v-model="localOpen"
                class="menu-dropdown"
            >
                <!-- Dropdown items to be displayed if the item is a button -->
                <slot name="dropdown-items" />
            </b-collapse>
        </div>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faChevronDown, faChevronUp} from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import uniqueId from 'lodash/uniqueId';

    library.add(faChevronDown, faChevronUp);

    /**
     * @group Layout
     * A component to display a Menu Item; to be used inside a `Menu`'s
     * `default` slot.
     */
    export default {
        name: 'MenuItem',
        components: {
            FontAwesomeIcon,
        },
        provide() {
            return {internal: this.internal};
        },
        props: {
            /**
             * When set to `true`, places the component in the active state with active styling
             */
            active: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * When set to `true`, places the component in the disabled state with disabled styling
             */
            disabled: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * Manually forces the item's dropdown to stay open
             */
            forceOpen: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * When set, turns the component into a custom/external link and sets the `href`
             * attribute of that link
             */
            href: {
                type: String,
                required: false,
                // `undefined`
                default: undefined,
            },
            /**
             * Sets the 'id' attribute on the button
             */
            id: {
                type: String,
                default: () => {
                    return `menuItem${uniqueId()}`;
                }
            },
            /**
             * When set to `true`, places the component in the internal state with internal styling
             */
            internal: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * Manually sets the open state of the item's dropdown
             */
            open: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * Sets the `rel` attribute of the custom/external link
             */
            rel: {
                type: String,
                required: false,
                // `undefined`
                default: undefined,
            },
            /**
             * Sets the `target` attribute of the custom/external link
             */
            target: {
                type: String,
                required: false,
                // `undefined`
                default: undefined,
            },
            /**
             * When set, turns the component into a `router-link`/internal link and sets the
             * `to` prop of that link
             */
            to: {
                type: [String, Object],
                required: false,
                // `undefined`
                default: undefined,
            },
        },
        data() {
            return {
                localOpen: this.forceOpen ? true : this.open,
            };
        },
        watch: {
            open: function(val) {
                if (!this.forceOpen) {
                    this.localOpen = val;
                }
            },
        },
        methods: {
            hasDropdown() {
                return !!this.$slots['dropdown-items'];
            },
            handleClick(event, id = null, hasDropdown = false) {
                if (!this.forceOpen) {
                    this.localOpen = !this.localOpen;
                }

                /**
                 * Emitted when the item is clicked; to be used when toggling the dropdown
                 * or performing an action on the parent when the item is clicked
                 */
                this.$emit('click', event);

                /**
                 * Same as the `click` event
                 */
                this.$emit('toggled', event);

                if (hasDropdown) {
                    this.$root.$emit('bv::toggle::collapse', `${id}Dropdown`);
                }
            },
        },
    };
</script>

<style lang="sass" scoped>
    .menu-item:hover
        cursor: pointer
</style>
